import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { User, FileText, Upload, Download, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { useDeal } from '../contexts/DealContext';
import { BuyerFinancialAnalysis } from './BuyerFinancialAnalysis';
import { BuyerFinancialAnalysisSimple } from './BuyerFinancialAnalysisSimple';
import { BuyerFinancialAnalysisTest } from './BuyerFinancialAnalysisTest';
import { BuyerFinancialAnalysisMinimal } from './BuyerFinancialAnalysisMinimal';
import { BuyerFinancialAnalysisDebug } from './BuyerFinancialAnalysisDebug';
import { BuyerProfileForm } from './BuyerProfileForm';

interface DealBuyerAnalysisManagerProps {
  className?: string;
}

export function DealBuyerAnalysisManager({ className = '' }: DealBuyerAnalysisManagerProps) {
  const { dealId } = useParams<{ dealId: string }>();
  const { selectedDeal, availableDeals, selectDeal } = useDeal();
  const [activeTab, setActiveTab] = useState('analysis');
  const [message, setMessage] = useState('');

  // Handle deal selection when URL changes
  useEffect(() => {
    if (!dealId) return;
    
    // Check if the selected deal matches the URL parameter
    if (!selectedDeal || selectedDeal.deal_id !== dealId) {
      // Try to find the deal in available deals and select it
      const deal = availableDeals.find(d => d.deal_id === dealId);
      if (deal) {
        selectDeal(deal);
      }
    }
  }, [dealId, selectedDeal, availableDeals, selectDeal]);

  // Check if we have a valid deal selected
  if (!dealId) {
    return <Navigate to="/deals" replace />;
  }

  // SECURITY: Block read-only users from accessing Buyer Analysis
  // This page should NEVER be accessible to users who signed NDAs
  if (selectedDeal?.access_type === 'read') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            Buyer Analysis is not available for read-only users. This page is restricted to deal creators and administrators.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while deal is being selected
  if (!selectedDeal || selectedDeal.deal_id !== dealId) {
    const deal = availableDeals.find(d => d.deal_id === dealId);
    if (deal) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading deal...</p>
          </div>
        </div>
      );
    } else {
      return <Navigate to="/deals" replace />;
    }
  }

  const tabs = [
    { id: 'analysis', label: 'Financial Analysis', icon: User },
    { id: 'profile', label: 'Buyer Profile', icon: FileText },
    { id: 'simple', label: 'Simple Analysis', icon: User },
    { id: 'test', label: 'Test Analysis', icon: User },
    { id: 'minimal', label: 'Minimal Analysis', icon: User },
    { id: 'debug', label: 'Debug Analysis', icon: User }
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'analysis':
        return <BuyerFinancialAnalysis />;
      case 'profile':
        return <BuyerProfileForm />;
      case 'simple':
        return <BuyerFinancialAnalysisSimple />;
      case 'test':
        return <BuyerFinancialAnalysisTest />;
      case 'minimal':
        return <BuyerFinancialAnalysisMinimal />;
      case 'debug':
        return <BuyerFinancialAnalysisDebug />;
      default:
        return <BuyerFinancialAnalysis />;
    }
  };

  return (
    <div className={`py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8 ${className}`}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedDeal.business_name}</h1>
        <p className="text-gray-600">Buyer Financial Analysis</p>
        {selectedDeal.description && (
          <p className="text-sm text-gray-500 mt-2">{selectedDeal.description}</p>
        )}
      </div>

      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{message}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6">
          {renderActiveTab()}
        </div>
      </div>

      {/* Deal-Specific Information */}
      <div className="mt-8 bg-blue-50 rounded-xl border border-blue-200 p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Deal Context
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-blue-700">Business Name:</p>
            <p className="font-medium text-blue-900">{selectedDeal.business_name}</p>
          </div>
          {selectedDeal.deal_value && (
            <div>
              <p className="text-sm text-blue-700">Deal Value:</p>
              <p className="font-medium text-blue-900">
                {new Intl.NumberFormat('en-US', { 
                  style: 'currency', 
                  currency: selectedDeal.currency, 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 0 
                }).format(selectedDeal.deal_value)}
              </p>
            </div>
          )}
          <div className="md:col-span-2">
            <p className="text-sm text-blue-700">Analysis Context:</p>
            <p className="text-blue-900">
              This buyer financial analysis is specific to the {selectedDeal.business_name} deal. 
              All financial data, documents, and analysis results are isolated to this deal 
              and will not affect other deals in the system.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
